// Workspace v2 auth context (HttpOnly cookie pivot · no localStorage)
// D3: session lives in HttpOnly cookie set by backend · JS never touches token.
// User identity is in-memory only · refreshed via GET /me on app mount + on demand.

import {
  createContext,
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";
import {
  AuthApiError,
  fetchMe,
  logoutServer,
  refreshSession,
  type UserPayload,
} from "./api";
import { authStore } from "./authStore";
import { queryClient } from "../queryClient";
import {
  SESSION_EXPIRY_WARNING_SECONDS,
  SessionExpiryDialog,
} from "./SessionExpiryDialog";

interface AuthState {
  user: UserPayload | null;
  isAuthenticated: boolean;
  setUser: (user: UserPayload | null) => void;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);
const AUTH_SESSION_CHANNEL = "velocity-workspace.auth-session";
const RENEWAL_SETTLE_TIMEOUT_MS = 9_000;

interface AuthSessionMessage {
  type: "renewed" | "signed_out";
}

type SessionVerification = "valid" | "invalid" | "superseded" | "unavailable";
type PromiseSettlement = "fulfilled" | "rejected" | "timed_out";

function isAuthSessionMessage(value: unknown): value is AuthSessionMessage {
  return (
    typeof value === "object"
    && value !== null
    && "type" in value
    && (value.type === "renewed" || value.type === "signed_out")
  );
}

function waitForPromiseSettlement(
  promise: Promise<void>,
  timeoutMs = RENEWAL_SETTLE_TIMEOUT_MS,
): Promise<PromiseSettlement> {
  return new Promise((resolve) => {
    let settled = false;
    const finish = (result: PromiseSettlement): void => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeoutId);
      resolve(result);
    };
    const timeoutId = window.setTimeout(() => finish("timed_out"), timeoutMs);
    void promise.then(
      () => finish("fulfilled"),
      () => finish("rejected"),
    );
  });
}

export interface AuthProviderProps {
  children: ReactNode;
  /** Test/integration seam; production uses a full page navigation to the SSO entry point. */
  onSessionExpired?: (loginUrl: string) => void;
}

export function sessionDeadlineFromUser(
  user: UserPayload | null,
  receivedAtMs = Date.now(),
): number | null {
  if (user?.session_expires_at == null) return null;
  if (!Number.isFinite(user.session_expires_at)) return null;
  if (user.server_time != null && Number.isFinite(user.server_time)) {
    const requestStartedAtMs = (
      user.session_request_started_at_ms != null
      && Number.isFinite(user.session_request_started_at_ms)
    ) ? user.session_request_started_at_ms : receivedAtMs;
    return requestStartedAtMs + Math.max(0, user.session_expires_at - user.server_time) * 1000;
  }
  return user.session_expires_at * 1000;
}

export function sessionExpiredLoginUrl(location: Pick<Location, "pathname" | "search" | "hash">): string {
  const requested = `${location.pathname}${location.search}${location.hash}`;
  const safeReturnTo = (
    requested.startsWith("/")
    && !requested.startsWith("//")
    && !requested.startsWith("/workspace/login")
    && !/[\r\n]/.test(requested)
  ) ? requested : "/workspace/";
  return `/workspace/login?returnTo=${encodeURIComponent(safeReturnTo)}`;
}

export function AuthProvider({ children, onSessionExpired }: AuthProviderProps): ReactElement {
  const [user, setUserState] = useState<UserPayload | null>(null);
  const [sessionDeadlineMs, setSessionDeadlineMs] = useState<number | null>(null);
  const [hydrated, setHydrated] = useState<boolean>(false);
  const expirationHandledRef = useRef(false);
  const renewalPromiseRef = useRef<Promise<void> | null>(null);
  const sessionGenerationRef = useRef(0);
  const sessionChannelRef = useRef<BroadcastChannel | null>(null);

  // Router guards run outside React, so publish the session snapshot before
  // scheduling the React state update. This prevents post-login navigation
  // from racing a stale `false` value in authStore.
  const setUser = useCallback((nextUser: UserPayload | null): void => {
    sessionGenerationRef.current += 1;
    const nextIdentity = nextUser?.sub ?? null;
    if (authStore.cacheIdentity !== nextIdentity || nextIdentity === null) {
      // Cancel and discard protected queries before another identity can render them.
      queryClient.clear();
      authStore.cacheIdentity = nextIdentity;
    }
    authStore.isAuthenticated = nextUser !== null;
    if (nextUser !== null) expirationHandledRef.current = false;
    setUserState(nextUser);
    setSessionDeadlineMs(sessionDeadlineFromUser(nextUser));
  }, []);

  useEffect(() => {
    // On mount, check whether a session cookie exists by hitting /me
    fetchMe()
      .then((u) => {
        setUser(u); // null if 401 · UserPayload if 200
        setHydrated(true);
      })
      .catch(() => {
        // Network error · treat as unauthenticated for now
        setUser(null);
        setHydrated(true);
      });
  }, [setUser]);

  const redirectToLogin = useCallback((): void => {
    const loginUrl = sessionExpiredLoginUrl(window.location);
    if (onSessionExpired) onSessionExpired(loginUrl);
    else window.location.assign(loginUrl);
  }, [onSessionExpired]);

  const redirectExpiredSession = useCallback((expectedGeneration?: number): void => {
    if (
      expectedGeneration !== undefined
      && sessionGenerationRef.current !== expectedGeneration
    ) return;
    if (expirationHandledRef.current) return;
    expirationHandledRef.current = true;
    setUser(null);
    // Do not call /logout here. Another tab may have renewed the shared HttpOnly
    // cookie while this tab's old-cookie /me request was in flight. Login will
    // transparently resume if a newer valid cookie exists.
    redirectToLogin();
  }, [redirectToLogin, setUser]);

  const clearServerSessionAfterRenewal = useCallback(async (): Promise<void> => {
    const pendingRenewal = renewalPromiseRef.current;
    if (pendingRenewal) await waitForPromiseSettlement(pendingRenewal);
    await logoutServer().catch(() => undefined);
  }, []);

  const invalidateLocalSession = useCallback((broadcast: boolean): void => {
    expirationHandledRef.current = true;
    setUser(null);
    if (broadcast) {
      const message: AuthSessionMessage = { type: "signed_out" };
      sessionChannelRef.current?.postMessage(message);
    }
  }, [setUser]);

  const signOutAndRedirect = useCallback((): void => {
    if (expirationHandledRef.current) return;
    invalidateLocalSession(true);
    void clearServerSessionAfterRenewal()
      .finally(redirectToLogin);
  }, [clearServerSessionAfterRenewal, invalidateLocalSession, redirectToLogin]);

  useEffect(() => {
    if (typeof BroadcastChannel === "undefined") return;

    const channel = new BroadcastChannel(AUTH_SESSION_CHANNEL);
    sessionChannelRef.current = channel;
    let active = true;

    channel.onmessage = (event: MessageEvent<unknown>): void => {
      if (!isAuthSessionMessage(event.data) || expirationHandledRef.current) return;

      if (event.data.type === "signed_out") {
        invalidateLocalSession(false);
        void clearServerSessionAfterRenewal().finally(redirectToLogin);
        return;
      }

      // Invalidate any old deadline check immediately, then verify the shared
      // cookie rather than trusting cross-tab message data as authentication.
      sessionGenerationRef.current += 1;
      const expectedGeneration = sessionGenerationRef.current;
      void fetchMe()
        .then((latestUser) => {
          if (!active || sessionGenerationRef.current !== expectedGeneration) return;
          const latestDeadline = sessionDeadlineFromUser(latestUser);
          if (latestUser && latestDeadline !== null && latestDeadline > Date.now()) {
            setUser(latestUser);
          }
        })
        .catch(() => undefined);
    };

    return () => {
      active = false;
      if (sessionChannelRef.current === channel) sessionChannelRef.current = null;
      channel.close();
    };
  }, [clearServerSessionAfterRenewal, invalidateLocalSession, redirectToLogin, setUser]);

  const renewSession = useCallback((): Promise<void> => {
    if (renewalPromiseRef.current) return renewalPromiseRef.current;
    const expectedGeneration = sessionGenerationRef.current;
    const renewal = refreshSession()
      .then((renewedUser) => {
        if (
          expirationHandledRef.current
          || sessionGenerationRef.current !== expectedGeneration
        ) return;
        setUser(renewedUser);
        const message: AuthSessionMessage = { type: "renewed" };
        sessionChannelRef.current?.postMessage(message);
      })
      .catch((error: unknown) => {
        if (error instanceof AuthApiError && error.status === 401) signOutAndRedirect();
        throw error;
      })
      .finally(() => {
        renewalPromiseRef.current = null;
      });
    renewalPromiseRef.current = renewal;
    return renewal;
  }, [setUser, signOutAndRedirect]);

  const verifyServerSession = useCallback(async (
    expectedGeneration: number,
    attempts: number,
  ): Promise<SessionVerification> => {
    let lastAttemptFailed = false;
    for (let attempt = 0; attempt < attempts; attempt += 1) {
      let latestUser: UserPayload | null = null;
      lastAttemptFailed = false;
      try {
        latestUser = await fetchMe();
      } catch {
        lastAttemptFailed = true;
        // A bounded retry below distinguishes a transient failure from expiry.
      }

      if (sessionGenerationRef.current !== expectedGeneration) return "superseded";
      const latestDeadline = sessionDeadlineFromUser(latestUser);
      if (latestUser && latestDeadline !== null && latestDeadline > Date.now()) {
        setUser(latestUser);
        return "valid";
      }
    }
    return lastAttemptFailed ? "unavailable" : "invalid";
  }, [setUser]);

  const handleDeadlineReached = useCallback(async (): Promise<void> => {
    const expectedGeneration = sessionGenerationRef.current;
    // If Continue was clicked on the final tick, let that single in-flight renewal
    // settle before deciding the session is over.
    const pendingRenewal = renewalPromiseRef.current;
    if (pendingRenewal) {
      const settlement = await waitForPromiseSettlement(pendingRenewal);
      if (settlement === "fulfilled") return;
      // Revalidate below after a failure/timeout. A second tab may have renewed
      // the shared cookie, while an unreachable server must still lead to sign-in.
    }

    if (sessionGenerationRef.current !== expectedGeneration) return;

    // Confirm once more after an expired/stale response. The second request starts
    // after the first completes, so it observes a cookie renewed in another tab even
    // when BroadcastChannel delivery is delayed.
    const verification = await verifyServerSession(expectedGeneration, 2);
    if (verification === "valid" || verification === "superseded") return;
    redirectExpiredSession(expectedGeneration);
  }, [redirectExpiredSession, verifyServerSession]);

  const logout = useCallback(async (): Promise<void> => {
    invalidateLocalSession(true);
    await clearServerSessionAfterRenewal();
  }, [clearServerSessionAfterRenewal, invalidateLocalSession]);

  useEffect(() => {
    if (user === null || sessionDeadlineMs === null) return;
    let checking = false;

    const revalidateNearExpiry = async (): Promise<void> => {
      if (
        checking
        || renewalPromiseRef.current !== null
        || sessionDeadlineMs - Date.now() > SESSION_EXPIRY_WARNING_SECONDS * 1000
      ) return;
      checking = true;
      const expectedGeneration = sessionGenerationRef.current;
      try {
        const verification = await verifyServerSession(expectedGeneration, 2);
        if (verification === "invalid") {
          redirectExpiredSession(expectedGeneration);
        }
      } catch {
        // A transient focus-time check must not sign the user out before the real deadline.
      } finally {
        checking = false;
      }
    };

    const onFocus = (): void => { void revalidateNearExpiry(); };
    const onVisibilityChange = (): void => {
      if (document.visibilityState === "visible") void revalidateNearExpiry();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [redirectExpiredSession, sessionDeadlineMs, user, verifyServerSession]);

  // Don't render children until /me completes — prevents flash of unauthenticated UI
  if (!hydrated) {
    return <></>;
  }

  // Publish the hydrated snapshot for the router's beforeLoad gate (runs before
  // children/RouterProvider render, so it's current when beforeLoad fires).
  authStore.isAuthenticated = user !== null;

  return (
    <AuthContext.Provider value={{ user, isAuthenticated: user !== null, setUser, logout }}>
      <Fragment key={user?.sub ?? "signed-out"}>{children}</Fragment>
      <SessionExpiryDialog
        deadlineMs={sessionDeadlineMs}
        onContinue={renewSession}
        onExpired={handleDeadlineReached}
        onSignOut={signOutAndRedirect}
      />
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
